/**
 * @param {object} ugcConfig  UGC configuration object
 * @return True if the ugc configuration points to an engage datasource
 */
export function isEngage(ugcConfig) {
    return _.get(ugcConfig, 'ugcSource', '') === "ENGAGE";
}

/**
 * Return an array of all the posts ids
 * @param items Items in a UGC
 * @return Array of posts ids
 */
export function getPostIds(items) {
    const ids = new Set();

    if (!_.isArray(items)) {
        return [];
    }

    for (let i = 0; i < items.length; i++) {
        ids.add(items[i].id);
    }

    return Array.from(ids);
}

/**
 * Return a list of posts with duplicates removed
 * @param {Array} items Curalate posts (items)
 * @returns {Array} posts without duplicates
 */
export function ugcPostsWithoutDuplicates(items) {
    const ids = new Set();

    return _.filter(items, (i) => {
        if (ids.has(i.id)) {
            return false;
        }

        ids.add(i.id);
        return true;
    });
}

/**
 * Merge 2 sets of UGC posts, without duplicates
 *
 * Other curalate data (bookmark, reel id, etc) will be taken from
 *  the ugc posts
 *
 */
export function mergeFeaturedPosts(featuredPosts, ugcPosts) {
    Array.prototype.unshift.apply(ugcPosts.items, featuredPosts.items);
    ugcPosts.items = ugcPostsWithoutDuplicates(ugcPosts.items);
    return ugcPosts;
}

/**
 * Build a Curalate Query dictionary
 * @param {object} state The current state of the redux store
 * @param {object} ugcConfig  UGC configuration object
 */
export function buildUgcQuery(state, ugcConfig) {
    // TODO: Move this to python??

    const config = {
        reelId: ugcConfig.reelId,
        galleryId: ugcConfig.galleryId,
        limit: ugcConfig.pageSize,
        locale: _.get(state, 'core.language', 'en-US'),
        offset: ugcConfig.offset,
        ugcSource: ugcConfig.ugcSource,
        isHearts: ugcConfig.isHearts,
        isUploads: ugcConfig.isUploads
    };

    if (ugcConfig.productStyleNumber && ugcConfig.ugcSource === "ENGAGE") {
        config.productId = ugcConfig.productStyleNumber;
    } else if (ugcConfig.productId && ugcConfig.ugcSource === "DASH") {
        config.productId = ugcConfig.productId;
    }

    if (ugcConfig.filterValue) {
        config.filters = ugcConfig.filterValue;
    }

    if (ugcConfig.bookmark) {
        config.bookmark = ugcConfig.bookmark;
    }

    return config;
}

/**
 * @param {object} ugcConfig UGC configuration object
 * @returns {boolean} True if the ugcConfig contains a featured filter
 */
export function hasFeaturedFilter(ugcConfig) {
    return Boolean(ugcConfig.featuredFilter) && Boolean(ugcConfig.featuredPageSize);
}

/**
 * Convert a UGC Config with a featured filter
 *  into a UGC Config without a featured that can be passed into
 *  the buildCuralateQuery function
 * @param {object} config UGC Configuration object
 */
export function convertFeaturedUgcConfig(config) {
    const convertedConfig = _.clone(config);

    convertedConfig.filterValue = config.featuredFilter;
    convertedConfig.pageSize = config.featuredPageSize;
    return convertedConfig;
}

/**
 * Get the product records from a UGC tile
 * @param {object} store Redux store object
 * @param {number} ugcIndex Index of the UGC component.
 * @returns {array} array of product records to feed into a quickshop product generator
 */
export function getUgcTrayProductRecords(store, ugcIndex) {
    const products = _.get(store.getState(), `core.ugc.${ugcIndex}.tray.products`, []);

    return _.map(products, (product) => product.product);
}

/**
 * Determine the number of tiles in a row on the UGC gallery based on
 *  the device characteristics
 * @param  {object} deviceCharacteristics The properties of the device viewing the content
 * @returns {number} Number of tiles in the row
 */
export function determineNumTilesInRow(deviceCharacteristics) {
    let tilesInRow = 2;

    if (deviceCharacteristics.is_tablet) {
        tilesInRow = 4;
    } else if (deviceCharacteristics.is_large) {
        tilesInRow = 6;
    }

    return tilesInRow;
}

 /**
  * We need to know where the product tray should go on UGC pages.  Through science, this function determines the appropriate tile.
  * @param {number} tilesInRow Number of tiles in a row on the UGC gallery
  * @param {number} clickedTileIndex index of the tile that was clicked
  * @param {number} numTiles number of tiles
  * @returns {number} index of the tile preceding where the tray should be inserted
  */
export function getNextEligibleTileForTray(tilesInRow, clickedTileIndex, numTiles) {
    // The number of tiles in each row changes based on screen size

    if (clickedTileIndex % tilesInRow === 0) {
        // The clicked tile is the right-most tile in the row
        return clickedTileIndex;
    }

    // increase the amount and return the index of the right-most tile in this row
    return Math.min(numTiles, clickedTileIndex + (tilesInRow - clickedTileIndex % tilesInRow));
}

export const ugcQuickshopOptions = {
    ownerView: false,
    shopperView: false,
    wrapPagination: false,
    showAddToWishlist: true,
    showAddToFavorites: true,
    showAddToRegistry: false,
    collapseAccordions: true,
    hideRequestSwatch: true,
    disablePaging: true
};
